import { useId } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ChevronDown, Clock } from 'lucide-react';
import { LEAD_DAY_OPTIONS, leadLabel } from './notification-catalog';
import { SendHourSelect } from './send-hour-select';
import type { NotificationSchedule } from '@hygge/shared';

const summary = (s: NotificationSchedule) =>
  [...s.leadDays]
    .sort((a, b) => b - a)
    .map((d) => (d === 0 ? 'day of' : `${d}d`))
    .join(' · ') +
  '  ·  ' +
  // 24-hour form, the same as the Send at picker. [HW-1071]
  s.sendTime;

export function SchedulePopover({
  schedule,
  onChange,
  disabled,
}: {
  schedule: NotificationSchedule;
  onChange: (next: { leadDays: number[]; sendTime: string }) => void;
  disabled?: boolean;
}) {
  const sendAtLabelId = useId();
  const toggle = (d: number) => {
    const has = schedule.leadDays.includes(d);
    const next = has ? schedule.leadDays.filter((x) => x !== d) : [...schedule.leadDays, d];
    if (next.length === 0) return; // never allow empty
    onChange({ leadDays: next, sendTime: schedule.sendTime });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className="inline-flex max-w-[180px] items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15 disabled:pointer-events-none disabled:opacity-50"
        >
          <span className="truncate">{summary(schedule)}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Remind before the event
        </p>
        <div className="mb-4 flex flex-wrap gap-1.5">
          {LEAD_DAY_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => toggle(d)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                schedule.leadDays.includes(d)
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:border-primary/40',
              )}
            >
              {leadLabel(d)}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3.5">
          <span id={sendAtLabelId} className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Send at
          </span>
          <SendHourSelect
            value={schedule.sendTime}
            onChange={(sendTime) => onChange({ leadDays: schedule.leadDays, sendTime })}
            disabled={disabled}
            labelId={sendAtLabelId}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
